import type { AnyRealtimeMessage } from "./types";

/**
 * A second reader for the realtime channel — for screens, not for the dock.
 *
 * WHY IT EXISTS. Every server message already reaches `useProcessStore`, which
 * turns long-running work into a card the user can watch from any page. That is
 * a SUMMARY, and it is the right shape for "your NFT is minting" while the user
 * walks away. It is the wrong shape for a screen that is looking at the very
 * thing an event is about: the asset page needs to know that THIS listing is
 * now on-chain, in the dialog the user is still standing in, and there is no
 * way to ask a summary store that question.
 *
 * ONE SOCKET, TWO CONSUMERS — never a second subscription. `RealtimeBridge`
 * owns the connection and hands each message to both. A component opening its
 * own channel would double the connections, and Reverb counts them.
 *
 * DELIBERATELY NOT A STORE. There is no state here and nothing to render from:
 * an event is a moment, not a value, and keeping the last one around invites a
 * screen to act on an event that happened before it opened. Subscribers get
 * what arrives while they are listening, and nothing else.
 */

type Listener = (message: AnyRealtimeMessage) => void;

const listeners = new Set<Listener>();

/**
 * Listen for realtime messages. Returns an unsubscribe function.
 *
 * The set is captured per call rather than iterated live, so a listener that
 * unsubscribes itself from inside its own handler — which is exactly what a
 * one-shot "wait for this event" does — cannot disturb the dispatch it is in.
 */
export function subscribeToRealtimeMessages(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Fan one message out. Called only by `RealtimeBridge`.
 *
 * A THROWING LISTENER MUST NOT SILENCE THE OTHERS, and must not take down the
 * socket handler that called this: the next message would then never arrive,
 * and the failure would look like the backend going quiet.
 */
export function publishRealtimeMessage(message: AnyRealtimeMessage): void {
  for (const listener of [...listeners]) {
    try {
      listener(message);
    } catch (error) {
      console.error("[realtime] a listener threw; the others still ran:", error);
    }
  }
}
